import {
    PaginationContent,
    PaginationEllipsis,
    PaginationFirst,
    PaginationItem,
    PaginationLast,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    Pagination as ShadcnPagination,
} from "@/components/shadcn/pagination";
import { getPaginationRange } from "@/lib/pagination";
import { cn } from "@/lib/utils";
import type Link from "next/link";
import type { ComponentProps, HTMLAttributes, MouseEvent } from "react";

export type PageHref = ComponentProps<typeof Link>["href"];

interface PaginationProps {
    pageCount: number;
    currentPage: number;
    /**
     * Reports the page the user picked; the caller decides what that means.
     * Without `hrefForPage` the click is only a report — the anchor never navigates.
     */
    onPageChange?: (page: number) => void;
    /** Where a page lives in the URL. Omit for pagination held in client state. */
    hrefForPage?: (page: number) => PageHref;
    /** Pages kept on each side of the current one; lower it in tight columns. */
    siblingCount?: number;
    /** The « » chevrons are droppable where space is tight; the 1 / last links remain. */
    showFirstLast?: boolean;
}

export default function Pagination({
    pageCount,
    currentPage,
    onPageChange,
    hrefForPage,
    siblingCount,
    showFirstLast = true,
    className,
}: HTMLAttributes<HTMLDivElement> & PaginationProps) {
    const pages = getPaginationRange(currentPage, pageCount, { siblingCount });
    const hasPrevPage = currentPage > 1;
    const hasNextPage = currentPage < pageCount;

    // The onClick is attached only when needed: from a server component this
    // renders with hrefForPage alone, and a function prop would be illegal to
    // pass across the server/client boundary to the link.
    const linkToPage = (page: number) => ({
        href: hrefForPage?.(page) ?? "#!",
        ...(onPageChange || !hrefForPage
            ? {
                  onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                      if (!hrefForPage) {
                          event.preventDefault();
                      }
                      onPageChange?.(page);
                  },
              }
            : {}),
    });

    // Unreachable pages keep an inert href so crawlers never see a page=0 or a
    // self-link, on top of the pointer-events guard that stops the click.
    const linkToPageIf = (enabled: boolean, page: number) =>
        enabled ? linkToPage(page) : { href: "#!" };

    return (
        <ShadcnPagination className={className}>
            <PaginationContent>
                {showFirstLast && (
                    <PaginationItem className="max-sm:hidden">
                        <PaginationFirst
                            {...linkToPageIf(hasPrevPage, 1)}
                            aria-disabled={!hasPrevPage}
                            className={cn(
                                !hasPrevPage &&
                                    "pointer-events-none opacity-50",
                            )}
                        />
                    </PaginationItem>
                )}
                <PaginationItem>
                    <PaginationPrevious
                        {...linkToPageIf(hasPrevPage, currentPage - 1)}
                        aria-disabled={!hasPrevPage}
                        className={cn(
                            !hasPrevPage && "pointer-events-none opacity-50",
                        )}
                    />
                </PaginationItem>
                {pages.map((item, index) =>
                    item === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={item}>
                            <PaginationLink
                                {...linkToPageIf(currentPage !== item, item)}
                                isActive={currentPage === item}
                                className={cn(
                                    currentPage === item &&
                                        "pointer-events-none",
                                )}
                            >
                                {item}
                            </PaginationLink>
                        </PaginationItem>
                    ),
                )}
                <PaginationItem>
                    <PaginationNext
                        {...linkToPageIf(hasNextPage, currentPage + 1)}
                        aria-disabled={!hasNextPage}
                        className={cn(
                            !hasNextPage && "pointer-events-none opacity-50",
                        )}
                    />
                </PaginationItem>
                {showFirstLast && (
                    <PaginationItem className="max-sm:hidden">
                        <PaginationLast
                            {...linkToPageIf(hasNextPage, pageCount)}
                            aria-disabled={!hasNextPage}
                            className={cn(
                                !hasNextPage &&
                                    "pointer-events-none opacity-50",
                            )}
                        />
                    </PaginationItem>
                )}
            </PaginationContent>
        </ShadcnPagination>
    );
}
